import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck, Copy } from "lucide-react";
import { LOGO } from "@/data/exercises";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.dev_token) {
        setResetLink(`${window.location.origin}/reinitialiser?token=${data.dev_token}`);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(resetLink);
    toast.success("Lien copié.");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F4F0E8] font-care">
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-sm p-8" data-testid="forgot-card">
        <img src={LOGO} alt="Passerelle" className="h-14 w-auto mb-4" />

        {!sent ? (
          <form onSubmit={submit} data-testid="forgot-form">
            <h2 className="font-serif-display text-2xl font-normal mb-1">Mot de passe oublié</h2>
            <p className="text-gray-500 mb-6">
              Saisissez votre e-mail. Nous vous enverrons un lien pour définir un nouveau mot de passe.
            </p>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="forgot-error">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="soignant@demo.fr"
                className="mt-1 h-12"
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 mt-6 text-base bg-emerald-700 hover:bg-emerald-800 rounded-xl"
              data-testid="forgot-submit-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer le lien"}
            </Button>
          </form>
        ) : (
          <div data-testid="forgot-success">
            <div className="flex items-center gap-2 text-emerald-700 mb-2">
              <MailCheck className="w-6 h-6" />
              <h2 className="font-serif-display text-2xl font-normal text-gray-900">Vérifiez vos e-mails</h2>
            </div>
            <p className="text-gray-500 mb-6">
              Si un compte existe pour cet e-mail, un lien de réinitialisation vient d'être envoyé.
            </p>

            {resetLink && (
              <div className="mb-6 rounded-lg bg-amber-50 border border-amber-200 p-4" data-testid="forgot-dev-link">
                <p className="text-xs font-semibold text-amber-800 mb-2">
                  Mode démonstration — aucun e-mail réel n'est envoyé. Utilisez ce lien :
                </p>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/reinitialiser?token=${resetLink.split("token=")[1]}`}
                    className="text-emerald-700 font-semibold text-sm break-all underline"
                    data-testid="forgot-open-reset-link"
                  >
                    Réinitialiser mon mot de passe
                  </Link>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="ml-auto shrink-0 text-amber-700 hover:text-amber-900"
                    title="Copier le lien"
                    data-testid="forgot-copy-link"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <Link
          to="/connexion"
          className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-700"
          data-testid="forgot-back-login"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>
      </div>
    </div>
  );
}
